// Lightweight London time helpers without external deps.

const ZONE = 'Europe/London';
const EXPLICIT_OFFSET_SUFFIX = /(?:Z|[+-]\d{2}:\d{2})$/i;

type LondonWallTime = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

const LONDON_PARTS_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const isFiniteNumber = (value: number): boolean => Number.isFinite(value);

const isValidUtcParts = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  millisecond: number,
): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day, hour, minute, second, millisecond));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day &&
    probe.getUTCHours() === hour &&
    probe.getUTCMinutes() === minute &&
    probe.getUTCSeconds() === second &&
    probe.getUTCMilliseconds() === millisecond
  );
};

const getPart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number => {
  const value = Number(parts.find((part) => part.type === type)?.value || '0');
  return isFiniteNumber(value) ? value : 0;
};

const getLondonOffsetMs = (utcInstantMs: number): number => {
  const parts = LONDON_PARTS_FORMATTER.formatToParts(new Date(utcInstantMs));
  const zonedAsUtcMs = Date.UTC(
    getPart(parts, 'year'),
    getPart(parts, 'month') - 1,
    getPart(parts, 'day'),
    getPart(parts, 'hour'),
    getPart(parts, 'minute'),
    getPart(parts, 'second'),
    0,
  );
  return zonedAsUtcMs - utcInstantMs;
};

const londonWallTimeToUtcInstant = (wallTime: LondonWallTime): Date => {
  const naiveUtcMs = Date.UTC(
    wallTime.year,
    wallTime.month - 1,
    wallTime.day,
    wallTime.hour,
    wallTime.minute,
    wallTime.second,
    wallTime.millisecond,
  );
  let offsetMs = getLondonOffsetMs(naiveUtcMs);
  let resolvedUtcMs = naiveUtcMs - offsetMs;

  // Re-check once to avoid DST boundary drift.
  const nextOffsetMs = getLondonOffsetMs(resolvedUtcMs);
  if (nextOffsetMs !== offsetMs) {
    offsetMs = nextOffsetMs;
    resolvedUtcMs = naiveUtcMs - offsetMs;
  }

  return new Date(resolvedUtcMs);
};

const parseDateOnly = (value: string): LondonWallTime | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!isValidUtcParts(year, month, day, 23, 59, 0, 0)) return null;
  return { year, month, day, hour: 23, minute: 59, second: 0, millisecond: 0 };
};

const parseDateTimeWithoutOffset = (value: string): LondonWallTime | null => {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
  );
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] || '0');
  const millisecond = Number((match[7] || '0').padEnd(3, '0'));

  if (!isValidUtcParts(year, month, day, hour, minute, second, millisecond)) return null;
  return { year, month, day, hour, minute, second, millisecond };
};

const buildLondonInstant = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (!trimmed.includes('T')) {
    const dateOnly = parseDateOnly(trimmed);
    if (!dateOnly) return null;
    return londonWallTimeToUtcInstant(dateOnly);
  }

  if (EXPLICIT_OFFSET_SUFFIX.test(trimmed)) {
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }

  const noOffsetDateTime = parseDateTimeWithoutOffset(trimmed);
  if (!noOffsetDateTime) return null;
  return londonWallTimeToUtcInstant(noOffsetDateTime);
};

export const parseLondon = (value?: string): Date | null => {
  if (!value) return null;
  return buildLondonInstant(value);
};

export const isPastLondon = (value?: string): boolean => {
  const dt = parseLondon(value);
  if (!dt) return false;
  return dt.getTime() < Date.now();
};

export const formatLondonDateTime = (value?: string): string | null => {
  const dt = parseLondon(value);
  if (!dt) return null;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: ZONE,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dt);
};

export const londonNow = () => new Date();

export const formatLondonIsoDateTime = (now = londonNow()): string => {
  const instantMs = Math.floor(now.getTime() / 1000) * 1000;
  const parts = LONDON_PARTS_FORMATTER.formatToParts(new Date(instantMs));
  const offsetMinutes = Math.round(getLondonOffsetMs(instantMs) / 60_000);
  const offsetSign = offsetMinutes < 0 ? '-' : '+';
  const absoluteOffsetMinutes = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, '0');
  const offsetRemainder = String(absoluteOffsetMinutes % 60).padStart(2, '0');
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    String(getPart(parts, type)).padStart(2, '0');

  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value(
    'minute',
  )}:${value('second')}${offsetSign}${offsetHours}:${offsetRemainder}`;
};

export const getLondonDateParts = (now = londonNow()) => {
  const parts = LONDON_PARTS_FORMATTER.formatToParts(now);
  return {
    year: getPart(parts, 'year'),
    month: getPart(parts, 'month'),
    day: getPart(parts, 'day'),
  };
};

export const getLondonMonthDay = (now = londonNow()) => {
  const { month, day } = getLondonDateParts(now);
  return { month, day };
};
